/* eslint-disable @typescript-eslint/naming-convention */
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactModel, IContactForm } from '@boxx/contacts-core';
import { IBoxxGenericFormEmitter } from '@boxx/core';
import {
  CountryCodePickerComponent,
  CountryPickerComponent,
  LocationPickerComponent,
} from '@boxx/shared-components';
import { ModalController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { COUNTRIES_DATA } from 'src/assets/countriesData';
import { APP_CONSTANTS } from 'src/constants/constants';
import { ContactPickerTypeModalComponent } from '../contact-picker-type-modal/contact-picker-type-modal';

@Component({
  selector: 'boxx-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss'],
})
export class ContactFormComponent implements OnInit, OnDestroy {
  @Output() boxxChange = new EventEmitter<any>();

  @Input() set contact(payload: ContactModel) {
    if (!this.contactForm) {
      this.initForm();
    }

    if (payload) {
      this.myContact = payload;

      this.patchForm(payload);

      this.originalValues = this.contactForm.value;

      this.onFieldChange();
    }
  }

  get contact() {
    return this.myContact;
  }

  contactForm: FormGroup;
  typeClass = 'not-specified';

  phoneMask = APP_CONSTANTS.PHONE_MASK;

  country = { name: '', flag: '' };

  types = [
    { key: 'CLIENT', value: 'Client' },
    { key: 'PROSPECT', value: 'Prospect' },
    { key: 'NOT_SPECIFIED', value: 'Not qualified' },
  ];

  private translations: any = {};
  private destroyed$ = new Subject<boolean>();
  private myContact: ContactModel;
  private originalValues: ContactModel;

  constructor(
    private modalCtrl: ModalController,
    private popoverController: PopoverController,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    translate: TranslateService
  ) {
    translate
      .get(['CONTACTS_MODULE'])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((t: any) => {
        this.translations = t;
        this.types[0].value = t.CONTACTS_MODULE.CONTACT_TYPES.client;
        this.types[1].value = t.CONTACTS_MODULE.CONTACT_TYPES.prospect;
        this.types[2].value = t.CONTACTS_MODULE.CONTACT_TYPES.not_specified;
      });

    this.initForm();
  }

  ngOnInit() { }

  initForm() {
    this.contactForm = this.formBuilder.group({
      id: [''],
      name: ['', Validators.required],
      last_name: [''],
      countryName: [''],
      country_code: [''],
      phone: [
        '',
        [Validators.required], //Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)
      ],
      phone_code: [''],
      email: [''],
      street_address: [''],
      type: ['NOT_SPECIFIED', Validators.required],
      typeName: [this.translations.CONTACTS_MODULE.CONTACT_TYPES.not_specified],
      city: [''],
    });
  }

  validaTelefono(event) {
    event.target.value = event.target.value.replace(/[^0-9]+/g, "");
    this.contactForm.patchValue({
      phone: event.target.value.substring(0, 10),
    });
  }

  validaTelefonoChange(event) {
    event.target.value = event.target.value.replace(/[^0-9]+/g, "");
    event.target.value = event.target.value.substring(0, 10);
    this.contactForm.patchValue({
      phone: event.target.value.substring(0, 10),
    });
    this.onFieldChange()
  }

  patchForm(contact: ContactModel) {
    const selectedKey = Object.keys(COUNTRIES_DATA).filter(
      (key) => COUNTRIES_DATA[key].alpha3Code === this.contact.countryCode
    )[0];

    this.country.flag = selectedKey;

    this.contactForm.patchValue({
      id: contact.id,
      name: contact.name,
      last_name: contact.lastName,
      phone: contact.phone.replace(/\s/g, ''),
      email: contact.email,
      type: contact.type,
      typeName:
        this.translations.CONTACTS_MODULE.CONTACT_TYPES[
        contact.type.toLocaleLowerCase()
        ],
      street_address: contact.streetAddress,
      countryName:
        COUNTRIES_DATA[selectedKey]?.translations.es ||
        COUNTRIES_DATA[selectedKey]?.name ||
        '',
      phone_code: COUNTRIES_DATA[selectedKey]?.callingCodes.length
        ? `+${COUNTRIES_DATA[selectedKey]?.callingCodes[0]}`
        : '',
    });

    this.cdr.detectChanges();

    this.typeClass = contact.type.toLocaleLowerCase().replace(/_/g, '-');
  }

  async openTypePicker() {
    const modal = await this.modalCtrl.create({
      component: ContactPickerTypeModalComponent,
    });

    modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.contactForm.get('type').patchValue(data.data.key);
      this.contactForm.get('typeName').patchValue(data.data.value);
      this.typeClass = data.data.class;
      this.onFieldChange();
    }
  }

  async openCountryPicker() {
    const modal = await this.modalCtrl.create({
      component: CountryPickerComponent,
      componentProps: {
        selectedCountry: this.country.flag,
      },
    });

    modal.present();

    const { data: countryData } = await modal.onDidDismiss();

    if (!countryData) {
      return;
    }

    if (countryData.callingCodes?.length > 1) {
      const popover = await this.popoverController.create({
        component: CountryCodePickerComponent,
        componentProps: { codes: countryData.callingCodes },
        // event: ev,
        translucent: true,
        backdropDismiss: false,
      });
      await popover.present();
      const { data: selectedCode } = await popover.onDidDismiss();

      this.contactForm.patchValue({
        phone_code: `+${selectedCode}`,
      });
    } else {
      if (countryData.callingCodes[0]) {
        this.contactForm.patchValue({
          phone_code: `+${countryData.callingCodes[0]}`,
        });
      }
    }

    this.country.flag = countryData.flag;
    this.country.name = countryData.translations?.es || countryData.name;
    this.contactForm.patchValue({
      countryName: this.country.name,
      country_code: countryData.alpha3Code,
    });
    this.onFieldChange();
    this.cdr.detectChanges();
  }

  async openPlacePicker() {

    const modal = await this.modalCtrl.create({
      component: LocationPickerComponent,
      // componentProps: {}
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();

    if (data) {
      this.contactForm.get('street_address').patchValue(data.description);
      this.onFieldChange();
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  close() {
    this.modalCtrl.dismiss(/*{someData: ...} */);
  }

  onFieldChange() {
    const values = this.contactForm.value;

    let hasChanges = Object.keys(values).some((key) => {
      const originalValue = this.originalValues[key];
      const newValue = this.contactForm.get(key).value;

      return newValue !== originalValue;
    });

    if(this.contactForm.valid && values.id === null){
      hasChanges = true;
    }

    this.boxxChange.emit({
      hasChanges,
      data: values,
      valid: this.contactForm.valid,
    });
    this.cdr.detectChanges();
  }
}

export type IContactFormEmitter = IBoxxGenericFormEmitter<IContactFormProps>;

export interface IContactFormProps extends IContactForm {
  countryName: string;
  typeName: string;
}
